// src/pages/BoardPackRenderPage.jsx
// Opened only by the board pack render worker (backend repo, report-worker/), never by users.
// Loads one board pack as the requesting user and reports its state on
// window.__BOARDPACK_RENDER = { state: "loading" | "ready" | "error", totalPages, batchPages,
// progress, error } so the worker knows when every page has its data and can be
// captured. With job.range = { from, to } only that slice of the pages is shown.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import BoardPack from "@/components/BoardPack/BoardPack";
import { installNetworkTracker } from "@/lib/networkTracker";

const IDLE_MS = 4000; // no request for this long → all page data is loaded
const SETTLE_MS = 1500; // then give charts time to finish animating

// The worker injects the job before the page loads; query params are for manual checks
const readJob = () => {
  const injected = window.__BOARDPACK_RENDER_JOB;
  if (injected?.contract) return injected;
  const params = new URLSearchParams(window.location.search);
  const contract = params.get("contract");
  const from = params.get("from");
  return contract
    ? {
        contract,
        year: params.get("year"),
        month: params.get("month"),
        comments: null,
        range: from !== null ? { from: Number(from), to: Number(params.get("to")) } : null,
      }
    : null;
};

const publish = (patch) => {
  window.__BOARDPACK_RENDER = { ...(window.__BOARDPACK_RENDER || {}), ...patch };
};

export default function BoardPackRenderPage() {
  const [job] = useState(readJob);
  const net = useMemo(() => installNetworkTracker(), []);
  const batchPagesRef = useRef(null);
  const [error, setError] = useState(job ? null : "No board pack to render");

  const onRenderStatus = useCallback(({ totalPages, batchPages, error: statusError }) => {
    if (totalPages !== undefined) {
      batchPagesRef.current = batchPages;
      publish({ totalPages, batchPages });
    }
    if (statusError) setError(statusError);
  }, []);

  useEffect(() => {
    if (error) publish({ state: "error", error });
  }, [error]);

  useEffect(() => {
    if (!job) return;
    publish({ state: "loading", progress: 0 });

    let quietSince = null;
    const timer = setInterval(() => {
      if (window.__BOARDPACK_RENDER?.state !== "loading") {
        clearInterval(timer);
        return;
      }

      const expected = batchPagesRef.current;
      const sections = document.querySelectorAll("[data-render-root] [data-pdf-section]").length;
      publish({
        progress: net.started ? Math.round((net.finished / net.started) * 100) : 0,
        requests: net.started,
      });

      const imagesLoaded = Array.from(document.images).every((img) => img.complete);
      const networkQuiet = net.inflight === 0 && Date.now() - net.lastChange >= IDLE_MS;

      // A slice past the last page is empty and ready right away
      if (expected === 0) {
        clearInterval(timer);
        publish({ state: "ready", progress: 100 });
        return;
      }

      if (expected > 0 && sections >= expected && networkQuiet && imagesLoaded) {
        quietSince ??= Date.now();
        if (Date.now() - quietSince >= SETTLE_MS) {
          clearInterval(timer);
          document.fonts.ready.then(() => publish({ state: "ready", progress: 100 }));
        }
      } else {
        quietSince = null;
      }
    }, 500);

    return () => clearInterval(timer);
  }, [job, net]);

  if (!job) return null;

  return (
    <div style={{ background: "#fff", minHeight: "100vh" }}>
      <BoardPack renderJob={job} onRenderStatus={onRenderStatus} />
    </div>
  );
}
